import useEcho from "@/hooks/use-echo.hook.ts";
import { MagnifyingGlass } from "@/icons/MagnifyingGlass.tsx";
import { MicrochipAI } from "@/icons/MicrochipAI.tsx";
import api from "@/lib/api.ts";
import { getPhotoDownloadUrl, Outputs } from "@/lib/helpers.ts";
import { PhotoModel, PhotosResponse } from "@/models/photo.model.ts";
import { getPhotos } from "@/services/photo.service.ts";
import useUserStore from "@/store/user.store.ts";
import { PhotoGrid } from "@/ui/shared/PhotoGrid.tsx";
import { PhotoLightBox } from "@/ui/shared/PhotoLightBox.tsx";
import { PhotoMarker } from "@/ui/shared/PhotoMarker";
import {
  Button,
  Card,
  CloseButton,
  ScrollArea,
  SegmentedControl,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import {
  useDebouncedValue,
  useDisclosure,
  useScrollIntoView,
  useViewportSize,
} from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useInView } from "react-intersection-observer";
import { useParams } from "react-router";

type Filter = "all" | "starred" | "liked";

export function Editor() {
  const echo = useEcho();
  const { webSocketsUserId } = useUserStore();

  useEffect(() => {
    if (echo && webSocketsUserId) {
      echo.private(`editor.${webSocketsUserId}`).listen("PhotosUpdated", () => {
        refetch();
      });
    }

    return () => {
      if (echo && webSocketsUserId) {
        echo
          .private(`editor.${webSocketsUserId}`)
          .stopListening("PhotosUpdated");
      }
    };
  }, [echo, webSocketsUserId]);

  const [mask, setMask] = useState<Blob | "">("");
  const [outputs, setOutputs] = useState<Outputs>("4");
  const [totalPhotos, setTotalPhotos] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 400);
  const [filter, setFilter] = useState<Filter>("all");
  const [activePhotoIndex, setActivePhotoIndex] = useState<number | null>(null);
  const { ref, inView } = useInView();
  const { scrollIntoView, targetRef } = useScrollIntoView<HTMLDivElement>({
    offset: 100,
  });
  const [viewerOpened, { open: openViewer, close: closeViewer }] =
    useDisclosure(false);

  const form = useForm<{
    prompt: string;
  }>({
    defaultValues: {
      prompt: "",
    },
  });
  const { height } = useViewportSize();
  const params = {
    limit: 1,
    "filter[name]": useParams().photoName,
  };

  const photoQuery = useQuery({
    queryKey: ["photo", params],
    queryFn: () => getPhotos(params),
  });

  const photo: PhotoModel | undefined = photoQuery.data?.data[0];

  const predictionMutation = useMutation<AxiosResponse<PhotosResponse>>({
    mutationFn: () => {
      return api.post(
        "/editor",
        {
          app: "editor",
          prompt: form.getValues("prompt"),
          outputs: outputs,
          mask: mask,
          input_photo_id: photo?.id,
        },
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
    },
  });

  const { data, isFetching, isError, fetchNextPage, hasNextPage, refetch } =
    useInfiniteQuery({
      queryKey: [
        "photos",
        {
          limit: 30,
          sort: "-created_at",
          "filter[filter]": filter,
          "filter[search]": debouncedSearch,
          "filter[app]": "editor",
          include: "lora",
        },
      ],
      queryFn: (settings) => {
        return getPhotos({
          limit: 30,
          sort: "-created_at",
          page: settings.pageParam,
          "filter[type]": "output",
          "filter[filter]": filter,
          "filter[search]": debouncedSearch,
          "filter[app]": "editor",
          include: "lora",
        });
      },
      initialPageParam: 1,
      getPreviousPageParam: (response) => {
        const meta = response.meta;

        if (meta.current_page === 1) {
          return null;
        }
        return meta.current_page - 1;
      },
      getNextPageParam: (response) => {
        const meta = response.meta;
        if (meta.current_page === meta.last_page) {
          return null;
        }
        return meta.current_page + 1;
      },
    });

  useEffect(() => {
    setTotalPhotos(data?.pages[0]?.meta.total ?? 0);
  }, [data]);

  useEffect(() => {
    refetch();
  }, [filter, refetch]);

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView, hasNextPage]);

  const handlePhotoClick = useCallback(
    (pageIndex: number, photoIndex: number) => {
      openViewer();
      const index = pageIndex * 30 + photoIndex;
      setActivePhotoIndex(index);
    },
    [openViewer, setActivePhotoIndex],
  );

  const handleGenerate = () => {
    predictionMutation.mutate();
    scrollIntoView({
      alignment: "start",
    });
    setFilter("all");
  };

  return (
    <>
      <div ref={targetRef} className="flex flex-col gap-5">
        <div className="relative grid grid-cols-1 sm:grid-cols-1 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 md:gap-x-4 gap-y-4">
          <div className="col-span-1 relative">
            <div
              style={{
                top: "4.75rem",
              }}
              className="sticky"
            >
              <ScrollArea
                h={height - 140}
                scrollbarSize={4}
                scrollbars="y"
                type="hover"
                offsetScrollbars={true}
              >
                <Card padding="lg" withBorder>
                  {photo && (
                    <Card.Section withBorder inheritPadding py="lg">
                      <PhotoMarker setMask={setMask} inputUrl={photo.url} />
                    </Card.Section>
                  )}
                  <Card.Section withBorder inheritPadding py="lg">
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-col gap-0.5">
                        <Text fw={500} size="md" component="label" mb={0}>
                          Number of variations
                        </Text>

                        <SegmentedControl
                          color="violet"
                          value={outputs}
                          onChange={(value) => setOutputs(value as Outputs)}
                          data={[
                            {
                              label: "1",
                              value: "1",
                            },
                            {
                              label: "2",
                              value: "2",
                            },
                            {
                              label: "3",
                              value: "3",
                            },
                            {
                              label: "4",
                              value: "4",
                            },
                          ]}
                        />
                      </div>

                      <Textarea
                        {...form.register("prompt")}
                        size="md"
                        label="Prompt"
                        placeholder="A man staring at the stars..."
                        autosize
                        minRows={5}
                      />
                    </div>
                  </Card.Section>

                  <Card.Section withBorder inheritPadding py="md">
                    <Button
                      disabled={predictionMutation.isPending}
                      loading={predictionMutation.isPending}
                      onClick={() => {
                        if (form.getValues("prompt").length < 5) {
                          notifications.show({
                            title: "Warning!",
                            message:
                              "Your prompt seems to be way too short. Try adding some more information.",
                            color: "orange",
                          });
                          return;
                        }

                        handleGenerate();
                      }}
                      variant="gradient"
                      gradient={{ from: "grape", to: "violet", deg: 90 }}
                      leftSection={<MicrochipAI />}
                      fullWidth
                    >
                      Generate
                    </Button>
                  </Card.Section>
                </Card>
              </ScrollArea>
            </div>
          </div>
          <div className="col-span-1 sm:col-span-1 md:col-span-2 lg:col-span-2 xl:col-span-3 2xl:col-span-4">
            <div className="flex flex-col md:flex-row mb-4 gap-2 w-full justify-start md:justify-between md:items-center">
              <Title order={3} fw={500}>
                Log ({totalPhotos || 0})
              </Title>

              <TextInput
                miw={300}
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
                leftSection={<MagnifyingGlass size={14} />}
                placeholder="Search photos by prompt..."
                rightSection={
                  search && (
                    <CloseButton
                      className="cursor-pointer"
                      aria-label="Clear input"
                      onClick={() => setSearch("")}
                    />
                  )
                }
              />

              <SegmentedControl
                color="grape"
                value={filter}
                onChange={(value) => setFilter(value as Filter)}
                data={[
                  {
                    label: "All",
                    value: "all",
                  },
                  {
                    label: "Starred",
                    value: "starred",
                  },
                  {
                    label: "Liked",
                    value: "liked",
                  },
                ]}
              />
            </div>

            <PhotoGrid
              data={data}
              form={form}
              handlePhotoClick={handlePhotoClick}
              isFetching={isFetching}
              isError={isError}
              inViewRef={ref}
            />
          </div>
        </div>
      </div>

      {data && (
        <PhotoLightBox
          viewerOpened={viewerOpened}
          activePhotoIndex={activePhotoIndex}
          closeViewer={closeViewer}
          slides={data.pages
            .map((page) => page.data.map((photo) => photo))
            .flat(1)
            .map((photo) => ({
              src: photo.url,
              downloadUrl: getPhotoDownloadUrl(photo),
              description: photo.prompt,
            }))}
        />
      )}
    </>
  );
}
